import React, { useState, useMemo } from 'react';
import { Button } from '@mui/material';
import styles from './GroupMembersView.module.scss';
import userWorkoutsRowStyles from '../Clients/UserWorkoutsRow.module.scss';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import EmptyState from '../composites/EmptyState';
import ListContent from '../composites/ListContent';
import GroupMemberRow from './GroupMemberRow';
import GroupComplianceList from './GroupComplianceList';
import AddGroupMembersDialog from './AddGroupMembersDialog';
import EditClientDialog from '../Clients/EditClientDialog';
import ConfirmationDialog from '../DialogWrappers/ConfirmationDialog';
import _ from 'lodash';
import { getFullName } from '../../utils';
import { useSessionData, useUserAction } from '../../providers';
import { Product, UserType } from '../../constants';
import { locales } from '../../constants/locale';
import TabSlider, { TabSliderTab } from '../composites/TabSlider';

import type { TrainerDashboardData } from '../../appState/appInitialState';
import type { Group, Member } from '../../types';

const texts = locales.en.components.clients.clientsContent;

enum GroupContentTabs {
  Members = 'Members',
  Compliance = 'Compliance',
}

interface GroupMembersViewProps {
  group: Group;
  allGymMembers: Member[];
  allGymTrainers: TrainerDashboardData[];
  userType: UserType;
  isLoading?: boolean;
  onBack?: () => void;
  onGoToInDepthUserPage?: (member: Member) => void;
  onGroupUpdated?: (group: Group) => void;
}

const GroupMembersView: React.FC<GroupMembersViewProps> = ({
  group,
  allGymMembers,
  allGymTrainers,
  userType,
  isLoading = false,
  onBack,
  onGoToInDepthUserPage,
  onGroupUpdated,
}) => {
  const { gymProducts } = useSessionData();
  const {
    updateGroup,
    updateMember,
    updateMemberSubscriptions,
    assignProgramAndRefreshMembers,
  } = useUserAction();
  const [selectedTab, setSelectedTab] = useState<GroupContentTabs>(
    GroupContentTabs.Members,
  );
  const [isAddMembersModalOpen, setIsAddMembersModalOpen] = useState(false);
  const [isEditClientModalOpen, setIsEditClientModalOpen] = useState(false);
  const [editedMember, setEditedMember] = useState<Member | null>(null);
  const [isAssignProgramDialogOpen, setIsAssignProgramDialogOpen] =
    useState(false);
  const [newMemberIdsToAssign, setNewMemberIdsToAssign] = useState<string[]>(
    [],
  );

  const groupMembers = useMemo(() => {
    return allGymMembers.filter((member) =>
      group.memberIds?.includes(member.id),
    );
  }, [allGymMembers, group.memberIds]);
  const handleEditClient = async (
    oldMemberData: Member,
    newMemberData: Member,
    selectedProduct: Product | '',
  ): Promise<{ success: boolean; emailAlreadyInUse?: boolean; pinAlreadyInUse?: boolean }> => {
    const updateResult = await updateMember(oldMemberData, newMemberData);
    if (!updateResult.success) {
      return updateResult;
    }
    const subscriptionUpdateSuccess = await updateMemberSubscriptions(
      oldMemberData,
      newMemberData,
      selectedProduct,
      gymProducts,
    );
    if (!subscriptionUpdateSuccess) {
      return { success: false };
    }
    setIsEditClientModalOpen(false);
    return { success: true };
  };

  const handleRemoveMemberFromGroup = async (member: Member) => {
    const updatedGroup = {
      ...group,
      memberIds: group.memberIds?.filter((id) => id !== member.id) ?? [],
    };

    const success = await updateGroup(group, updatedGroup);
    if (success) {
      onGroupUpdated?.(updatedGroup);
    }
  };

  const handleAddMembers = async (
    currentGroupData: Group,
    newGroupData: Group,
  ): Promise<boolean> => {
    const success = await updateGroup(currentGroupData, newGroupData);
    if (!success) {
      return false;
    }

    onGroupUpdated?.(newGroupData);

    const newMemberIds = _.difference(
      newGroupData.memberIds,
      currentGroupData.memberIds,
    );

    if (group.program && newMemberIds.length > 0) {
      setNewMemberIdsToAssign(newMemberIds);
      setIsAssignProgramDialogOpen(true);
    }

    setIsAddMembersModalOpen(false);
    return true;
  };

  const handleGoToInDepthUserPage = (member: Member) => {
    onGoToInDepthUserPage?.(member);
  };

  const renderGroupMembersInDepth = () => {
    return (
      <div className={styles.contentContainer}>
        <span className={styles.sectionTitle}>{texts.GROUP_MEMBERS}</span>
        <ListContent
          items={groupMembers}
          isLoading={isLoading}
          titles={['Athlete Name', 'eMail', 'Program', 'PIN', 'Options']}
          listHeaderClassName={userWorkoutsRowStyles.workoutsListGrid}
          ItemComponent={({ data }) => (
            <GroupMemberRow
              key={data.id}
              member={data}
              userType={userType}
              openEditClientModal={() => {
                setEditedMember(data);
                setIsEditClientModalOpen(true);
              }}
              onRemoveMember={() => handleRemoveMemberFromGroup(data)}
              onGoToInDepthUserPage={() => handleGoToInDepthUserPage(data)}
            />
          )}
          EmptyStateComponent={() => (
            <EmptyState
              icon={<AccountCircleRoundedIcon />}
              text="No Members in this Group"
            />
          )}
        />
      </div>
    );
  };

  const renderGroupComplianceInDepth = () => {
    return (
      <div className={styles.contentContainer}>
        <GroupComplianceList
          members={groupMembers}
          userType={userType}
          isLoading={isLoading}
          onEditClient={(member) => {
            setEditedMember(member);
            setIsEditClientModalOpen(true);
          }}
          onRemoveMember={handleRemoveMemberFromGroup}
          onGoToInDepthUserPage={handleGoToInDepthUserPage}
          isActive={selectedTab === GroupContentTabs.Compliance}
          sectionTitle={texts.PER_ATHELETE_METRICS}
        />
      </div>
    );
  };

  const rightActions = (
    <Button
      className={styles.addMembersBtn}
      variant="text"
      color="primary"
      disabled={isLoading}
      onClick={() => setIsAddMembersModalOpen(true)}
    >
      <PersonAddAlt1OutlinedIcon color="primary" />
      <span className={styles.addMembersBtnText}>Add Members</span>
    </Button>
  );

  const tabSliderTabs: TabSliderTab[] = [
    {
      label: GroupContentTabs.Members,
      content: renderGroupMembersInDepth(),
    },
    {
      label: GroupContentTabs.Compliance,
      content: renderGroupComplianceInDepth(),
    },
  ];

  const renderContent = () => {
    return (
      <TabSlider
        tabs={tabSliderTabs}
        initialTab={0}
        disabled={isLoading}
        rightActions={rightActions}
        activeTab={selectedTab === GroupContentTabs.Members ? 0 : 1}
        setActiveTab={(idx) =>
          setSelectedTab(tabSliderTabs[idx].label as GroupContentTabs)
        }
      />
    );
  };

  const renderEditClientDialog = () => {
    if (!isEditClientModalOpen || !editedMember) return null;
    return (
      <EditClientDialog
        userType={userType}
        trainers={allGymTrainers.map(({ trainer }) => ({
          id: trainer.id,
          name: getFullName(trainer),
        }))}
        member={editedMember}
        gymMembers={allGymMembers}
        onSubmit={(newMemberData, newSelectedProduct) =>
          handleEditClient(editedMember, newMemberData, newSelectedProduct)
        }
        isOpen={isEditClientModalOpen}
        onClose={() => {
          setIsEditClientModalOpen(false);
          setEditedMember(null);
        }}
      />
    );
  };

  const renderAddMembersDialog = () => {
    return (
      <AddGroupMembersDialog
        group={group}
        allGymMembers={allGymMembers}
        allGymTrainers={allGymTrainers}
        isOpen={isAddMembersModalOpen}
        onSubmit={handleAddMembers}
        onClose={() => setIsAddMembersModalOpen(false)}
      />
    );
  };

  const renderAssignProgramDialog = () => {
    if (!group.program) return null;

    return (
      <ConfirmationDialog
        isOpen={isAssignProgramDialogOpen}
        onCancel={() => {
          setIsAssignProgramDialogOpen(false);
          setNewMemberIdsToAssign([]);
        }}
        onSubmit={async () => {
          if (group.program) {
            await assignProgramAndRefreshMembers(
              newMemberIdsToAssign,
              group.program,
            );
          }
          setIsAssignProgramDialogOpen(false);
          setNewMemberIdsToAssign([]);
        }}
        title="Assign Program?"
        text={`Would you like to add the new members to ${group.name}'s current program?`}
        submitText="Assign Program"
        cancelText="Not Now"
      />
    );
  };

  return (
    <div className={styles.container}>
      {renderContent()}
      {renderEditClientDialog()}
      {renderAddMembersDialog()}
      {renderAssignProgramDialog()}
    </div>
  );
};

export default GroupMembersView;
